import React, { useState } from 'react';

export default function ContactForm({ idPrefix = '' }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot field
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!formData.name || !formData.phone || !formData.email || !formData.description) {
      setStatus({ submitting: false, success: false, error: 'Venligst udfyld alle påkrævede felter.' });
      return;
    }

    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: '',
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Der opstod en fejl ved afsendelse. Prøv igen senere.');
      }
    } catch (error) {
      setStatus({ submitting: false, success: false, error: error.message });
    }
  };

  return (
    <div className="rounded-2xl border border-asphalt-200 bg-white p-6 shadow-sm md:p-8">
      <h3 className="font-display text-2xl font-bold text-asphalt-900">Få et uforpligtende tilbud</h3>
      <p className="mt-2 text-sm text-asphalt-600">
        Udfyld formularen, så vender vi tilbage til dig inden for 24 timer med et uforpligtende tilbud på dit projekt.
      </p>

      {status.success ? (
        <div className="mt-6 rounded-xl bg-olive-50 border border-olive-200 p-6 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-olive-100 text-olive-600">
            <i className="fa-solid fa-check text-xl"></i>
          </div>
          <h4 className="mt-4 font-display text-lg font-bold text-olive-800">Tak for din henvendelse!</h4>
          <p className="mt-2 text-sm text-olive-700">
            Vi har modtaget din besked og vil kontakte dig hurtigst muligt.
          </p>
          <button
            onClick={() => setStatus({ submitting: false, success: false, error: null })}
            className="mt-6 inline-flex items-center justify-center rounded-lg border border-olive-200 bg-white px-4 py-2 text-sm font-semibold text-olive-700 hover:bg-olive-50 transition-colors duration-200"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          {/* Honeypot field - absolutely hidden from humans */}
          <div className="sr-only absolute -z-50 overflow-hidden h-0 w-0">
            <label htmlFor={`${idPrefix}website_url`}>Hjemmeside URL (Udfyld ikke dette felt)</label>
            <input
              type="text"
              id={`${idPrefix}website_url`}
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div>
            <label htmlFor={`${idPrefix}name`} className="block text-sm font-semibold text-asphalt-800">
              Navn <span className="text-safety-orange">*</span>
            </label>
            <input
              type="text"
              id={`${idPrefix}name`}
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="mt-1.5 block w-full rounded-lg border border-asphalt-200 bg-transparent px-4 py-3 text-asphalt-900 placeholder-asphalt-400 focus:border-safety-orange focus:bg-transparent focus:ring-1 focus:ring-safety-orange focus:outline-none transition-all duration-200"
              placeholder="Dit fulde navn"
            />
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor={`${idPrefix}phone`} className="block text-sm font-semibold text-asphalt-800">
                Telefonnummer <span className="text-safety-orange">*</span>
              </label>
              <input
                type="tel"
                id={`${idPrefix}phone`}
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="mt-1.5 block w-full rounded-lg border border-asphalt-200 bg-transparent px-4 py-3 text-asphalt-900 placeholder-asphalt-400 focus:border-safety-orange focus:bg-transparent focus:ring-1 focus:ring-safety-orange focus:outline-none transition-all duration-200"
                placeholder="Tlf. nummer"
              />
            </div>

            <div>
              <label htmlFor={`${idPrefix}email`} className="block text-sm font-semibold text-asphalt-800">
                E-mail <span className="text-safety-orange">*</span>
              </label>
              <input
                type="email"
                id={`${idPrefix}email`}
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="mt-1.5 block w-full rounded-lg border border-asphalt-200 bg-transparent px-4 py-3 text-asphalt-900 placeholder-asphalt-400 focus:border-safety-orange focus:bg-transparent focus:ring-1 focus:ring-safety-orange focus:outline-none transition-all duration-200"
                placeholder="din@email.dk"
              />
            </div>
          </div>

          <div>
            <label htmlFor={`${idPrefix}description`} className="block text-sm font-semibold text-asphalt-800">
              Beskrivelse af opgaven <span className="text-safety-orange">*</span>
            </label>
            <textarea
              id={`${idPrefix}description`}
              name="description"
              required
              rows="4"
              value={formData.description}
              onChange={handleChange}
              className="mt-1.5 block w-full rounded-lg border border-asphalt-200 bg-transparent px-4 py-3 text-asphalt-900 placeholder-asphalt-400 focus:border-safety-orange focus:bg-transparent focus:ring-1 focus:ring-safety-orange focus:outline-none transition-all duration-200 resize-none"
              placeholder="Beskriv dit projekt, tidsramme og eventuelle specifikke ønsker..."
            ></textarea>
          </div>

          {status.error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
              <i className="fa-solid fa-triangle-exclamation mr-2"></i>
              {status.error}
            </div>
          )}

          <button
            type="submit"
            disabled={status.submitting}
            className="w-full inline-flex items-center justify-center rounded-lg bg-safety-orange px-6 py-3.5 text-base font-bold text-white shadow-md hover:bg-safety-orange-hover hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-safety-orange focus:ring-offset-2 disabled:opacity-50 transition-all duration-200 cursor-pointer"
          >
            {status.submitting ? (
              <>
                <i className="fa-solid fa-spinner animate-spin mr-2"></i>
                Sender...
              </>
            ) : (
              <>
                Send forespørgsel
                <i className="fa-solid fa-arrow-right ml-2"></i>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
